
import { useState } from "react";
import { TrendingUp, RefreshCw, Database, MapPin, Wifi, WifiOff } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { UsdaReportsSection } from "./UsdaReportsSection";
import { UsdaReportProcessor } from "./UsdaReportProcessor";
import { TerminalMarketPrices } from "./TerminalMarketPrices";
import { ProfileTerminalPrices } from "./ProfileTerminalPrices";
import { useUserFilters } from "@/hooks/useUserFilters";
import { useToast } from "@/hooks/use-toast";
import { testSupabaseConnection, diagnoseConnectionIssue, ConnectionTestResult } from "@/utils/supabaseConnectionTest";

export const Prices = () => {
  const { userProfile, userCrops, loading: userLoading } = useUserFilters();
  const [lastUpdated, setLastUpdated] = useState<Date>(new Date());
  const [connectionStatus, setConnectionStatus] = useState<ConnectionTestResult | null>(null);
  const [isTestingConnection, setIsTestingConnection] = useState(false);
  const { toast } = useToast();

  const handleRefresh = () => {
    setLastUpdated(new Date());
  };

  const testConnection = async () => {
    setIsTestingConnection(true);
    console.log("=== MANUAL CONNECTION TEST ===");
    
    try {
      const result = await testSupabaseConnection();
      setConnectionStatus(result);
      
      const diagnosis = diagnoseConnectionIssue(result);
      console.log("Connection diagnosis:", diagnosis);
      
      toast({
        title: result.success ? "Connection Healthy" : "Connection Issue",
        description: diagnosis,
        variant: result.success ? "default" : "destructive",
      });
      
    } catch (error) {
      console.error("Connection test error:", error);
      toast({
        title: "Connection Test Failed",
        description: "Unable to test connection",
        variant: "destructive",
      });
    } finally {
      setIsTestingConnection(false);
    }
  };

  if (userLoading) {
    return (
      <div className="p-4 space-y-4">
        <div className="text-center mb-6">
          <h1 className="text-2xl font-bold text-green-800 mb-2">Market Prices</h1>
          <p className="text-gray-600">Loading crop price data...</p>
        </div>
        <div className="animate-pulse space-y-4">
          <div className="h-32 bg-gray-200 rounded-lg"></div>
          <div className="h-64 bg-gray-200 rounded-lg"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 space-y-4">
      <div className="text-center mb-6">
        <div className="flex items-center justify-center space-x-2 mb-2">
          <h1 className="text-2xl font-bold text-green-800">Market Prices</h1>
          {userProfile?.location && (
            <span className="text-sm text-blue-600 flex items-center space-x-1">
              <MapPin size={14} />
              <span>{userProfile.location}</span>
            </span>
          )}
          <Button 
            onClick={handleRefresh} 
            variant="ghost" 
            size="sm" 
            className="text-green-600 hover:text-green-800"
          >
            <RefreshCw size={16} />
          </Button>
          
          {/* Connection Status Indicator */}
          <Button
            variant="outline"
            size="sm"
            onClick={testConnection}
            disabled={isTestingConnection}
            className="text-green-600 hover:text-green-800 border-green-300"
          >
            {isTestingConnection ? (
              <RefreshCw className="h-4 w-4 animate-spin" />
            ) : connectionStatus?.success ? (
              <Wifi className="h-4 w-4" />
            ) : (
              <WifiOff className="h-4 w-4" />
            )}
          </Button>
        </div>
        <p className="text-gray-600">Real-time crop pricing data from USDA and market sources</p>
        
        {/* Data freshness info */}
        <div className="flex items-center justify-center space-x-4 mt-2">
          <span className="text-xs text-blue-600 flex items-center space-x-1">
            <Database size={12} />
            <span>USDA API Integrated</span>
          </span>
          <span className="text-xs text-gray-500">
            Last Updated: {lastUpdated.toLocaleTimeString()}
          </span>
          {connectionStatus && (
            <span className="text-xs text-gray-500">
              Connection: {connectionStatus.success ? "Healthy" : "Issue"}
              {connectionStatus.latency && ` (${connectionStatus.latency}ms)`}
            </span>
          )}
        </div>
        
        {/* Connection Status Details */}
        {connectionStatus && !connectionStatus.success && (
          <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-700">{diagnoseConnectionIssue(connectionStatus)}</p>
          </div>
        )}
      </div>

      {/* Profile Terminal Market Prices Section */}
      <ProfileTerminalPrices />

      {/* Original Terminal Market Prices Section */}
      <TerminalMarketPrices />

      {/* USDA Reports Section */}
      <UsdaReportsSection />

      {/* USDA Data Processor (for admin/development) */}
      <UsdaReportProcessor />

      {/* Additional market insights */}
      <Card className="mt-6">
        <CardHeader>
          <CardTitle className="text-lg font-semibold text-green-800 flex items-center space-x-2">
            <TrendingUp size={20} />
            <span>Market Insights</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-green-50 p-4 rounded-lg border border-green-200">
              <h3 className="font-semibold text-green-800 mb-2">Profile Prices</h3>
              <p className="text-sm text-green-700">
                Personalized pricing data stored in your profile based on matched USDA reports and your crop preferences.
              </p>
            </div>
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <h3 className="font-semibold text-blue-800 mb-2">Terminal Markets</h3>
              <p className="text-sm text-blue-700">
                Wholesale pricing data from major commodity markets and distribution centers.
              </p>
            </div>
            <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
              <h3 className="font-semibold text-purple-800 mb-2">Retail Markets</h3>
              <p className="text-sm text-purple-700">
                Consumer pricing data from retail outlets and grocery stores.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
